// Workplace Communication Research (2026-02)
// /resources/workplace-communication-research — anonymous, Tally-hosted
// research participation. See the feature's PRD entry for the full
// spec. Three stages in one page, never a separate route per stage:
// "selection" (language + professional-role pickers) -> "questionnaire"
// (full-screen Tally iframe, beneath the site's own header, no footer)
// -> "completion" (thank-you + 2 CTAs). Full-screen flag toggling
// happens entirely in this component's own state; the footer/sticky-
// booking-CTA suppression for this one route lives in `SiteLayout.jsx`.
//
// This page has its OWN, in-content language dropdown (Dropdown A),
// deliberately independent of the site-wide URL-driven LanguageSwitcher
// in the Nav — selecting it updates ONLY this page's own copy/dropdown/
// button text/instructions and which Tally form opens, never the URL
// or the site chrome's language. SEO metadata (title/description/
// hreflang/canonical) still follows the real URL language via the
// normal `useLang()`/`PageMeta` path below, since that is what defines
// the 3 separately-indexable URLs.
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import PageMeta from "@/components/PageMeta";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ROUTES } from "@/config/links";
import { useLang, localizePath } from "@/i18n";
import en from "@/i18n/locales/en.json";
import el from "@/i18n/locales/el.json";
import pt from "@/i18n/locales/pt.json";
import { TALLY_FORM_IDS, TALLY_EMBED_ORIGIN, tallyEmbedSrc } from "./tallyConfig";
import { trackResearchStarted, trackResearchSubmitted } from "@/lib/researchTracking";
import { BookingPickerModal } from "./BookingPickerModal";

const RESEARCH_DICT = { en: en.workplaceCommunicationResearch, el: el.workplaceCommunicationResearch, "pt-BR": pt.workplaceCommunicationResearch };
const SITE_LANG_FOR_RESEARCH = { en: "en", el: "el", "pt-BR": "pt" };
const RESEARCH_LANG_FOR_SITE = { en: "en", el: "el", pt: "pt-BR" };

/** Measures the real, currently-rendered height of the site's fixed
 * header via ResizeObserver — never a guessed/fixed pixel value, so
 * "100dvh minus the actual header height" stays correct across
 * breakpoints without duplicating Nav's own responsive height logic. */
const useHeaderHeight = () => {
  const [height, setHeight] = useState(80);
  useEffect(() => {
    const header = document.querySelector("header");
    if (!header) return undefined;
    const update = () => setHeight(header.getBoundingClientRect().height);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(header);
    return () => observer.disconnect();
  }, []);
  return height;
};

const WorkplaceCommunicationResearch = () => {
  const { lang: siteLang } = useLang();
  const headerHeight = useHeaderHeight();

  const [stage, setStage] = useState("selection"); // selection | questionnaire | completion
  const [researchLang, setResearchLang] = useState(() => RESEARCH_LANG_FOR_SITE[siteLang] || "en");
  const [audience, setAudience] = useState(null);
  const [showBackConfirm, setShowBackConfirm] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [iframeLoading, setIframeLoading] = useState(true);

  const iframeRef = useRef(null);
  const activeFormIdRef = useRef(null);
  const activeNonceRef = useRef(null);
  const submittedRef = useRef(false);

  const rt = useCallback((key) => RESEARCH_DICT[researchLang]?.[key] ?? RESEARCH_DICT.en[key] ?? key, [researchLang]);
  const rl = useCallback((path) => localizePath(path, SITE_LANG_FOR_RESEARCH[researchLang] || "en"), [researchLang]);

  const activeFormId = audience ? TALLY_FORM_IDS[researchLang]?.[audience] : null;
  const bookingTriggerRef = useRef(null);

  useEffect(() => {
    // A stage change always starts at the top of the viewport — avoids
    // any scroll position carried over from the selection stage pushing
    // the fixed-position questionnaire content under the fixed header.
    window.scrollTo(0, 0);
  }, [stage]);

  const handleStart = () => {
    if (!audience || !activeFormId) return;
    const nonce = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    activeFormIdRef.current = activeFormId;
    activeNonceRef.current = nonce;
    submittedRef.current = false;
    setIframeLoading(true);
    // One intentional launch -> exactly one research_started. Never on
    // page render, never on a dropdown change — only this explicit click.
    trackResearchStarted({ language: researchLang, audience, formId: activeFormId, sessionNonce: nonce });
    setStage("questionnaire");
  };

  const requestBackToSelection = () => setShowBackConfirm(true);
  const confirmBackToSelection = () => {
    setShowBackConfirm(false);
    setStage("selection"); // researchLang/audience state is untouched -> selections preserved
  };

  useEffect(() => {
    if (stage !== "questionnaire") return undefined;

    const handleMessage = (event) => {
      // 1) origin, 2) source iframe, 3) safe parse, 4) exact event type,
      // 5) form_id matches the ACTIVE questionnaire, 6) dedupe — in that
      // order. No substring matching anywhere. The full payload (which
      // may contain answers) is never read beyond `payload.formId`, and
      // never logged.
      if (event.origin !== TALLY_EMBED_ORIGIN) return;
      if (!iframeRef.current || event.source !== iframeRef.current.contentWindow) return;
      let data;
      try {
        data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
      } catch {
        return;
      }
      if (!data || typeof data !== "object" || data.event !== "Tally.FormSubmitted") return;
      if (data.payload?.formId !== activeFormIdRef.current) return;
      if (submittedRef.current) return;
      submittedRef.current = true;
      trackResearchSubmitted({
        language: researchLang, audience, formId: activeFormIdRef.current, sessionNonce: activeNonceRef.current,
      });
      setStage("completion");
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [stage, researchLang, audience]);

  const canStart = Boolean(audience);

  return (
    <div data-testid="workplace-communication-research-page">
      <PageMeta titleKey="meta.wcr_title" descriptionKey="meta.wcr_description" path={ROUTES.workplaceCommunicationResearch} />

      {stage === "selection" && (
        <section
          className="mx-auto flex max-w-2xl flex-col justify-center px-6 pb-16"
          style={{ minHeight: `calc(100dvh - ${headerHeight}px)`, paddingTop: headerHeight + 32 }}
          data-testid="research-selection-stage"
        >
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground" data-testid="research-label">
            {rt("label")}
          </p>
          <h1 className="mt-3 font-serif text-4xl tracking-tight sm:text-5xl" data-testid="research-h1">{rt("h1")}</h1>

          <div className="mt-6 space-y-4 text-base leading-relaxed text-muted-foreground">
            <p>{rt("intro_1")}</p>
            <p>{rt("intro_2")}</p>
            <p>{rt("intro_3")}</p>
          </div>

          <div className="mt-6">
            <p className="text-sm font-semibold uppercase tracking-wide text-foreground">{rt("topics_label")}</p>
            <p className="mt-1 text-base text-muted-foreground">{rt("topics_text")}</p>
          </div>

          <p className="mt-8 text-sm font-medium text-foreground">{rt("instruction")}</p>

          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground" htmlFor="research-language-select">
                {rt("language_label")}
              </label>
              <Select value={researchLang} onValueChange={setResearchLang}>
                <SelectTrigger id="research-language-select" data-testid="research-language-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en" data-testid="research-language-option-en">{rt("language_option_en")}</SelectItem>
                  <SelectItem value="el" data-testid="research-language-option-el">{rt("language_option_el")}</SelectItem>
                  <SelectItem value="pt-BR" data-testid="research-language-option-pt-BR">{rt("language_option_pt")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground" htmlFor="research-role-select">
                {rt("role_label")}
              </label>
              <Select value={audience || undefined} onValueChange={setAudience}>
                <SelectTrigger id="research-role-select" data-testid="research-role-select">
                  <SelectValue placeholder={rt("role_placeholder")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="leaders" data-testid="research-role-option-leaders">{rt("role_leaders")}</SelectItem>
                  <SelectItem value="hr" data-testid="research-role-option-hr">{rt("role_hr")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Button
            size="lg"
            className="mt-8 w-full sm:w-auto"
            disabled={!canStart}
            onClick={handleStart}
            data-testid="research-start-button"
          >
            {rt("cta_start")}
          </Button>

          <p className="mt-6 text-sm text-muted-foreground" data-testid="research-privacy-note">{rt("privacy_note")}</p>
        </section>
      )}

      {stage === "questionnaire" && (
        <section
          className="fixed inset-x-0 bottom-0 w-full"
          style={{ top: headerHeight }}
          data-testid="research-questionnaire-stage"
        >
          <button
            type="button"
            onClick={requestBackToSelection}
            className="absolute right-3 top-3 z-10 rounded-md bg-background/90 px-3 py-1.5 text-xs font-medium text-muted-foreground shadow-sm backdrop-blur transition-colors hover:text-foreground"
            data-testid="research-back-button"
          >
            &larr; {rt("back_to_selection")}
          </button>

          {iframeLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-background" data-testid="research-iframe-loading">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">{rt("loading")}</span>
            </div>
          )}

          <iframe
            ref={iframeRef}
            key={activeFormId}
            src={tallyEmbedSrc(activeFormId)}
            title={rt("iframe_title")}
            onLoad={() => setIframeLoading(false)}
            className="h-full w-full border-0"
            data-testid="research-tally-iframe"
          />
        </section>
      )}

      {stage === "completion" && (
        <section
          className="mx-auto flex max-w-xl flex-col items-start justify-center px-6 pb-16 text-left"
          style={{ minHeight: `calc(100dvh - ${headerHeight}px)`, paddingTop: headerHeight + 32 }}
          data-testid="research-completion-stage"
        >
          <h1 className="font-serif text-3xl tracking-tight sm:text-4xl" data-testid="research-completion-heading">
            {rt("completion_heading")}
          </h1>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground" data-testid="research-completion-body">
            {rt("completion_body")}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild variant="outline" data-testid="research-cta-explore-tools">
              <Link to={rl(ROUTES.tools)}>{rt("cta_explore_tools")}</Link>
            </Button>
            <Button variant="secondary" ref={bookingTriggerRef} onClick={() => setShowBookingModal(true)} data-testid="research-cta-book-conversation">
              {rt("cta_book_conversation")}
            </Button>
          </div>
        </section>
      )}

      <AlertDialog open={showBackConfirm} onOpenChange={setShowBackConfirm}>
        <AlertDialogContent data-testid="research-back-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{rt("back_to_selection")}</AlertDialogTitle>
            <AlertDialogDescription>{rt("back_confirm")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="research-back-confirm-cancel">{rt("back_confirm_stay")}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmBackToSelection} data-testid="research-back-confirm-ok">
              {rt("back_confirm_leave")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <BookingPickerModal open={showBookingModal} onOpenChange={setShowBookingModal} rt={rt} rl={rl} triggerRef={bookingTriggerRef} />
    </div>
  );
};

export default WorkplaceCommunicationResearch;
